import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { action } from "typesafe-actions";
import { api } from "../../api/api";
import {
  getFileNameFromUrl,
  uploadMultipleFile,
} from "../../components/FileUpload/utils";
import { getUniqueId } from "../../helpers";
import { IQueryParams } from "../common/common.types";
import { getSearchQuery } from "../common/helpers";
import { IStoreState } from "../initialStoreState";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import {
  IApprovalFlow,
  IExcelPreview,
  IExpense,
  IExpenseApproval,
  IExpenseCatApproval,
  IExpenseCategory,
  IExpenseExcelPreview,
  IGroupedApprovalExpense,
  IHandleDynamicApproval,
} from "./expenseModule.types";
import { IDate } from "../../components/Table/hooks/useDateFilter";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { IFileUpload } from "../../components/FileUpload/FileUpload.type";
import { isArray } from "lodash";

// ********************** Fetch Expense List********************** //
export const FETCH_EXPENSE_LIST_PROGRESS = "FETCH_EXPENSE_LIST_PROGRESS";
export const FETCH_EXPENSE_LIST_SUCCESS = "FETCH_EXPENSE_LIST_SUCCESS";
export const FETCH_EXPENSE_LIST_FAILED = "FETCH_EXPENSE_LIST_FAILED";

export const fetchExpenseListProgress = () =>
  action(FETCH_EXPENSE_LIST_PROGRESS);
export const fetchExpenseListSuccess = (
  data: IExpense[],
  totalRecords: number,
) => action(FETCH_EXPENSE_LIST_SUCCESS, { data, totalRecords });
export const fetchExpenseListFailed = () => action(FETCH_EXPENSE_LIST_FAILED);

export const fetchExpenseListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    searchValue: IDatatableV2AdvancedSearchFilter,
    type: "EXPENSE" | "ADVANCE" | "VENDOR_PAYMENT",
    billing_company_uuid: string,
    searchByProject?: string,
    searchByCategory?: string,
    searchByExpenseFor?: string,
    searchByExpenseType?: string,
    expenseDate?: string,
    reportUuid?: string,
    unreported?: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      let url = `&billing_company_uuid=${billing_company_uuid}&expense_type=${type}`;

      if (reportUuid) {
        url += `&report_uuid=${reportUuid}`;
      }

      if (unreported) {
        url += `&unreported=${unreported}`;
      }

      console.log(searchValue, "searchValue");
      if (
        searchValue.length > 0 ||
        searchByProject ||
        expenseDate ||
        searchByCategory ||
        searchByExpenseFor ||
        searchByExpenseType
      ) {
        const advanceFilter = [...searchValue];
        if (searchByProject) {
          advanceFilter.push({
            column: ["project_name"],
            value: searchByProject,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }
        if (expenseDate) {
          advanceFilter.push({
            column: ["expense_date"],
            value: expenseDate,
            logicalOperator: "AND",
            operator: "EQUAL",
          });
        }
        if (searchByCategory) {
          advanceFilter.push({
            column: ["expense_category_name"],
            value: searchByCategory,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }
        if (searchByExpenseFor) {
          advanceFilter.push({
            column: ["user_name"],
            value: searchByExpenseFor,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }
        if (searchByExpenseType) {
          advanceFilter.push({
            column: ["expense_type"],
            value: searchByExpenseType,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }
        url += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
      }

      if (status !== "-1") {
        url += `&status=${status}`;
      }

      const finalUrl =
        `/expense/get-expense?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}` +
        url;

      dispatch(fetchExpenseListProgress());
      const res = await api.get(finalUrl);
      const data: IExpense[] = res.data.data;
      const totalRecords = res.data.totalRecords;
      dispatch(fetchExpenseListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchExpenseListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

// ********************** Fetch Single Expense ********************** //
export const FETCH_EXPENSE_PROGRESS = "FETCH_EXPENSE_PROGRESS";
export const FETCH_EXPENSE_SUCCESS = "FETCH_EXPENSE_SUCCESS";
export const FETCH_EXPENSE_FAILED = "FETCH_EXPENSE_FAILED";

export const fetchExpenseProgress = () => action(FETCH_EXPENSE_PROGRESS);
export const fetchExpenseSuccess = (data: IExpense) =>
  action(FETCH_EXPENSE_SUCCESS, { data });
export const fetchExpenseFailed = (errorMessage: string) =>
  action(FETCH_EXPENSE_FAILED, { errorMessage });

export const fetchExpenseAsync =
  (
    uuid: string,
    billing_company_uuid: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchExpenseProgress());
      let url = `/expense/get-expense?expense_uuid=${uuid}`;
      if (billing_company_uuid) {
        url += `&billing_company_uuid=${billing_company_uuid}`;
      }

      const res = await api.get(url);
      const data = res.data.data;

      if (data.length > 0) {
        let expenseData = data[0];
        expenseData.receipt = isArray(expenseData.receipt)
          ? expenseData.receipt
              .filter((item: any) => typeof item === "string")
              .map((file: string) => {
                return {
                  key: getUniqueId(),
                  type: "SERVER",
                  file: null,
                  name: getFileNameFromUrl(file || ""),
                  path: file,
                } as IFileUpload;
              })
          : [];
        dispatch(fetchExpenseSuccess(expenseData));
      } else {
        dispatch(fetchExpenseFailed("Oops! We couldn't find any records."));
      }
    } catch (err: any) {
      dispatch(fetchExpenseFailed("Something went to be wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

// ********************** Upsert Expense ********************** //
export const upsertExpenseAsync =
  (
    data: IExpense,
    onCallback: (isSuccess: boolean, customer?: IExpense) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const {
        create_ts,
        insert_ts,
        rowId,
        report_name,
        report_uuid,
        amount,
        requested_by_uuid,
        is_user_approver,
        approval_uuid,
        billing_company_branch_address_state,
        dynamic_approval_uuid,

        ...payload
      } = data;
      const asPayload = {
        title: data.expense_date,
      };
      // Corrected logic for mapping receipts
      const fileToUpload = isArray(payload.receipt)
        ? payload.receipt.filter((item) => item.type === "LOCAL")
        : [];
      if (fileToUpload.length > 0) {
        const { paths } = await uploadMultipleFile(
          fileToUpload,
          "REPORT",
          asPayload,
        );
        payload.receipt = payload.receipt
          .filter((item) => item.type === "SERVER" && item.path)
          .map((item) => item.path!)
          .concat(paths as string[]) as any;
      } else {
        payload.receipt = payload.receipt
          .filter((item) => item.type === "SERVER" && item.path)
          .map((item) => item.path!) as any;
      }

      dispatch(saveLoaderProgress());
      const res = await api.post("/expense/upsert-expense", {
        ...payload,
      });

      const result = res.data.data[0];

      let message = `${data.expense_type === "ADVANCE" ? "Advance" : "Expense"} created successfully!`;
      onCallback(true, result);
      dispatch(
        showMessage({
          type: "success",
          displayAs: "snackbar",
          message: message,
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// ********************** getUserAdvanceExpenseAmountAsync ********************** //
export const getUserAdvanceExpenseAmountAsync =
  (
    uuid: string,
    billing_company_uuid: string,
    onCallback: (isSuccess: boolean, advance: string) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const response = await api.get(
        `/expense/get-advance-amount?user_uuid=${uuid}&billing_company_uuid=${billing_company_uuid}`,
      );
      const result = response.data.data.pending_amount || 0;
      onCallback(true, result);
    } catch (err: any) {
      onCallback(false, "0");
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "No advance amount available",
          displayAs: "snackbar",
        }),
      );
    }
  };

// ********************** Clear Expense ********************** //

export const CLEAR_EXPENSE = "CLEAR_EXPENSE";
export const CLEAR_EXPENSE_STATE = "CLEAR_EXPENSE_STATE";

// ********************** Fetch Expense Category List********************** //
export const FETCH_EXPENSE_CATEGORY_LIST_PROGRESS =
  "FETCH_EXPENSE_CATEGORY_LIST_PROGRESS";
export const FETCH_EXPENSE_CATEGORY_LIST_SUCCESS =
  "FETCH_EXPENSE_CATEGORY_LIST_SUCCESS";
export const FETCH_EXPENSE_CATEGORY_LIST_FAILED =
  "FETCH_EXPENSE_CATEGORY_LIST_FAILED";

export const fetchExpenseCategoryListProgress = () =>
  action(FETCH_EXPENSE_CATEGORY_LIST_PROGRESS);
export const fetchExpenseCategoryListSuccess = (
  data: IExpenseCategory[],
  totalRecords: number,
) => action(FETCH_EXPENSE_CATEGORY_LIST_SUCCESS, { data, totalRecords });
export const fetchExpenseCategoryListFailed = () =>
  action(FETCH_EXPENSE_CATEGORY_LIST_FAILED);

export const fetchExpenseCategoryListAsync =
  (
    queryParams: IQueryParams,
    billing_company_uuid: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const searchQuery = getSearchQuery(queryParams);
      dispatch(fetchExpenseCategoryListProgress());
      let finalUrl = `/expense/get-expense-category${searchQuery}`;
      if (billing_company_uuid) {
        finalUrl += `&billing_company_uuid=${billing_company_uuid}`;
      }
      const res = await api.get(finalUrl);
      const data: IExpenseCategory[] = res.data.data;
      const totalRecords = res.data.totalRecords;
      dispatch(fetchExpenseCategoryListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchExpenseCategoryListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

// ********************** Fetch Single Expense Category********************** //
export const FETCH_EXPENSE_CATEGORY_PROGRESS =
  "FETCH_EXPENSE_CATEGORY_PROGRESS";
export const FETCH_EXPENSE_CATEGORY_SUCCESS = "FETCH_EXPENSE_CATEGORY_SUCCESS";
export const FETCH_EXPENSE_CATEGORY_FAILED = "FETCH_EXPENSE_CATEGORY_FAILED";

export const fetchExpenseCategoryProgress = () =>
  action(FETCH_EXPENSE_CATEGORY_PROGRESS);
export const fetchExpenseCategorySuccess = (data: IExpenseCategory) =>
  action(FETCH_EXPENSE_CATEGORY_SUCCESS, { data });
export const fetchExpenseCategoryFailed = (errorMessage: string) =>
  action(FETCH_EXPENSE_CATEGORY_FAILED, { errorMessage });

export const fetchExpenseCategoryAsync =
  (
    uuid: string,
    billing_company_uuid: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchExpenseCategoryProgress());
      let url = `/expense/get-expense-category?expense_category_uuid=${uuid}`;
      if (billing_company_uuid) {
        url += `&billing_company_uuid=${billing_company_uuid}`;
      }
      const res = await api.get(url);
      const data: IExpenseCategory[] = res.data.data;

      if (data.length > 0) {
        dispatch(fetchExpenseCategorySuccess(data[0]));
      } else {
        dispatch(
          fetchExpenseCategoryFailed("Oops! We couldn't find any records."),
        );
      }
    } catch (err: any) {
      dispatch(fetchExpenseCategoryFailed("Something went to be wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

// ********************** Upsert Expense Category********************** //
export const upsertExpenseCategoryAsync =
  (
    data: IExpenseCategory,
    onCallback: (isSuccess: boolean, category?: IExpenseCategory) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const {
        create_ts,
        insert_ts,
        rowId,
        billing_company_branch_address_state,

        ...payload
      } = data;

      dispatch(saveLoaderProgress());
      const res = await api.post("/expense/upsert-expense-category", payload);

      const result = res.data.data;

      let message = "Category created successfully!";
      onCallback(true, result);
      dispatch(
        showMessage({
          type: "success",
          displayAs: "snackbar",
          message: message,
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// ********************** Clear Expense Category********************** //

export const CLEAR_EXPENSE_CATEGORY = "CLEAR_EXPENSE_CATEGORY";
export const clearExpenseCategory = () => action(CLEAR_EXPENSE_CATEGORY);

// ********************** Fetch Expense Report List********************** //
export const FETCH_EXPENSE_REPORT_LIST_PROGRESS =
  "FETCH_EXPENSE_REPORT_LIST_PROGRESS";
export const FETCH_EXPENSE_REPORT_LIST_SUCCESS =
  "FETCH_EXPENSE_REPORT_LIST_SUCCESS";
export const FETCH_EXPENSE_REPORT_LIST_FAILED =
  "FETCH_EXPENSE_REPORT_LIST_FAILED";

export const fetchExpenseReportListProgress = () =>
  action(FETCH_EXPENSE_REPORT_LIST_PROGRESS);
export const fetchExpenseReportListSuccess = (
  data: IExpenseApproval[],
  totalRecords: number,
) => action(FETCH_EXPENSE_REPORT_LIST_SUCCESS, { data, totalRecords });
export const fetchExpenseReportListFailed = () =>
  action(FETCH_EXPENSE_REPORT_LIST_FAILED);

export const fetchExpenseReportListAsync =
  (queryParams: IQueryParams): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const searchQuery = getSearchQuery(queryParams);
      dispatch(fetchExpenseReportListProgress());
      let finalUrl = `/report/get-report${searchQuery}`;
      const res = await api.get(finalUrl);
      const data: IExpenseApproval[] = res.data.data;
      const totalRecords = res.data.totalRecords;
      dispatch(fetchExpenseReportListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchExpenseReportListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

// ********************** Fetch Approval BOM List********************** //
export const FETCH_APPROVAL_EXPENSE_LIST_PROGRESS =
  "FETCH_APPROVAL_EXPENSE_LIST_PROGRESS";
export const FETCH_APPROVAL_EXPENSE_LIST_SUCCESS =
  "FETCH_APPROVAL_EXPENSE_LIST_SUCCESS";
export const FETCH_APPROVAL_EXPENSE_LIST_FAILED =
  "FETCH_APPROVAL_EXPENSE_LIST_FAILED";

export const fetchApprovalExpenseListProgress = () =>
  action(FETCH_APPROVAL_EXPENSE_LIST_PROGRESS);
export const fetchApprovalExpenseListSuccess = (
  data: IGroupedApprovalExpense[],
  totalRecords: number,
) =>
  action(FETCH_APPROVAL_EXPENSE_LIST_SUCCESS, {
    data,
    totalRecords,
  });
export const fetchApprovalExpenseListFailed = () =>
  action(FETCH_APPROVAL_EXPENSE_LIST_FAILED);

const execGroupDataByCreatedByName = (
  data: IExpenseApproval[],
): IGroupedApprovalExpense[] => {
  const groupedData = Object.values(
    data.reduce((acc, obj) => {
      //@ts-ignore
      if (!acc[obj.created_by_name]) {
        //@ts-ignore
        acc[obj.created_by_name] = {
          id: obj.created_by_name,
          ...obj,
          childs: [obj],
        };
      } else {
        //@ts-ignore
        acc[obj.created_by_name].childs.push(obj);
      }
      return acc;
    }, {}),
  );
  return groupedData as IGroupedApprovalExpense[];
};
const execGroupDataByCreatedByProjectName = (
  data: IExpenseApproval[],
): IGroupedApprovalExpense[] => {
  const groupedData = Object.values(
    data.reduce((acc, obj) => {
      //@ts-ignore
      if (!acc[obj.project_name]) {
        //@ts-ignore
        acc[obj.project_name] = {
          id: obj.project_name,
          ...obj,
          childs: [obj],
        };
      } else {
        //@ts-ignore
        acc[obj.project_name].childs.push(obj);
      }
      return acc;
    }, {}),
  );
  return groupedData as IGroupedApprovalExpense[];
};

export const fetchApprovalExpenseListAsync =
  (
    queryParams: IQueryParams,
    expenseTypeValue: string,
    table_name: string,
    reportUuid?: string,
    unreported?: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      let searchQuery = getSearchQuery(queryParams);
      if (reportUuid) {
        searchQuery += `&report_uuid=${reportUuid}`;
      }

      if (unreported) {
        searchQuery += `&unreported=${unreported}`;
      }

      if (table_name) {
        searchQuery += `&table_name=${table_name}`;
      }

      dispatch(fetchApprovalExpenseListProgress());
      let finalUrl = `/approval/get-approval${searchQuery}`;
      const res = await api.get(finalUrl);
      const data: IExpenseApproval[] = res.data.data;
      const filteredData = !expenseTypeValue
        ? data.filter((item) => item.expense_type === "EXPENSE")
        : data.filter((item) => item.expense_type === expenseTypeValue);

      const finalData =
        expenseTypeValue === "EXPENSE"
          ? execGroupDataByCreatedByName(filteredData)
          : execGroupDataByCreatedByProjectName(filteredData);

      dispatch(
        fetchApprovalExpenseListSuccess(finalData, res.data.totalRecords),
      );
    } catch (err: any) {
      dispatch(fetchApprovalExpenseListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

// ********************** Fetch Single Expense Report ********************** //
export const FETCH_EXPENSE_REPORT_PROGRESS = "FETCH_EXPENSE_REPORT_PROGRESS";
export const FETCH_EXPENSE_REPORT_SUCCESS = "FETCH_EXPENSE_REPORT_SUCCESS";
export const FETCH_EXPENSE_REPORT_FAILED = "FETCH_EXPENSE_REPORT_FAILED";

export const fetchExpenseReportProgress = () =>
  action(FETCH_EXPENSE_REPORT_PROGRESS);
export const fetchExpenseReportSuccess = (data: IExpenseApproval) =>
  action(FETCH_EXPENSE_REPORT_SUCCESS, { data });
export const fetchExpenseReportFailed = (errorMessage: string) =>
  action(FETCH_EXPENSE_REPORT_FAILED, { errorMessage });

// export const fetchExpenseReportAsync =
// 	(uuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
// 	async (dispatch, getState) => {
// 		try {
// 			dispatch(fetchExpenseReportProgress());
// 			const res = await api.get(`/report/get-report?report_uuid=${uuid}`);
// 			const data: IExpenseApproval[] = res.data.data;

// 			if (data.length > 0) {
// 				dispatch(fetchExpenseReportSuccess(data[0]));
// 			} else {
// 				dispatch(
// 					fetchExpenseReportFailed("Oops! We couldn't find any records.")
// 				);
// 			}
// 		} catch (err: any) {
// 			dispatch(fetchExpenseReportFailed("Something went to be wrong!"));
// 			dispatch(
// 				showMessage({
// 					type: "error",
// 					message: err.response.data.message,
// 					displayAs: "snackbar",
// 				})
// 			);
// 		}
// 	};

// ********************** Upsert Expense Report********************** //
export const upsertExpenseReportAsync =
  (
    data: IExpenseApproval,
    onCallback: (isSuccess: boolean, customer?: IExpense) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const {
        create_ts,
        insert_ts,
        rowId,
        approval_uuid,
        is_user_approver,
        requested_by_uuid,
        remark,
        ...payload
      } = data;

      dispatch(saveLoaderProgress());
      const res = await api.post("/report/upsert-report", payload);

      const result = res.data.data[0];

      let message = "Report created successfully!";
      onCallback(true, result);
      dispatch(
        showMessage({
          type: "success",
          displayAs: "snackbar",
          message: message,
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_EXPENSE_CAT_APPROVAL_LIST_PROGRESS =
  "FETCH_EXPENSE_CAT_APPROVAL_LIST_PROGRESS";
export const FETCH_EXPENSE_CAT_APPROVAL_LIST_SUCCESS =
  "FETCH_EXPENSE_CAT_APPROVAL_LIST_SUCCESS";
export const FETCH_EXPENSE_CAT_APPROVAL_LIST_FAILED =
  "FETCH_EXPENSE_CAT_APPROVAL_LIST_FAILED";

export const fetchExpenseCatApprovalListProgress = () =>
  action(FETCH_EXPENSE_CAT_APPROVAL_LIST_PROGRESS);
export const fetchExpenseCatApprovalListSuccess = (
  data: IExpenseCatApproval[],
  totalRecords: number,
) => action(FETCH_EXPENSE_CAT_APPROVAL_LIST_SUCCESS, { data, totalRecords });
export const fetchExpenseCatApprovalListFailed = () =>
  action(FETCH_EXPENSE_CAT_APPROVAL_LIST_FAILED);

export const fetchExpenseCatApprovalListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    categoryId: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchExpenseCatApprovalListProgress());

      const res = await api.get(
        `/dynamicApproval/get-approval-count?&dynamic_table_name=latest_expense_category&pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&dynamic_uuid=${categoryId}`,
      );
      const data: IExpenseCatApproval[] = res.data.data;

      dispatch(fetchExpenseCatApprovalListSuccess(data, res.data.totalRecords));
    } catch (err: any) {
      dispatch(fetchExpenseCatApprovalListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const upsertExpenseCatApproval =
  (
    data: IExpenseCatApproval,
    onCallback: (isSuccess: boolean) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    const { create_ts, level, rowId, insert_ts, ...rest } = data;
    try {
      dispatch(saveLoaderProgress());
      await api.post("/dynamicApproval/insert-approval-count", rest);
      let message = "Approval saved successfully.";
      dispatch(
        showMessage({
          type: "success",
          message: message,
          displayAs: "snackbar",
        }),
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// ********************** Clear Expense Report********************** //

export const CLEAR_EXPENSE_REPORT = "CLEAR_EXPENSE_REPORT";
export const CLEAR_EXPENSE_LIST_STATE = "CLEAR_EXPENSE_LIST_STATE";
export const CREATE_ADVANCE_EXPENSE_STATE = "CREATE_ADVANCE_EXPENSE_STATE";
export const clearExpenseReport = () => action(CLEAR_EXPENSE_REPORT);

// Default Clear State

export const clearExpense = () => action(CLEAR_EXPENSE);
export const clearExpenseState = () => action(CLEAR_EXPENSE_STATE);
export const clearExpenseListState = () => action(CLEAR_EXPENSE_LIST_STATE);
export const createAvanceExpenseState = () =>
  action(CREATE_ADVANCE_EXPENSE_STATE);

export const convertFinanceToCleared =
  (
    data: {
      expense_uuid: string;
      status: "FINANCE_APPROVED" | "CLEARED" | "FINANCE_APPROVAL_REQUESTED";
    }[],
    onCallback: (isSuccess: boolean, data?: IExpense) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());

      // Prepare the payload to match the expected format

      const payload = {
        expense_uuids: data.map((item) => item.expense_uuid),
      };

      const res = await api.post(
        "/expense/convert-finance-to-cleared",
        payload,
      );

      dispatch(
        showMessage({
          type: "success",
          message: "Product(s) moved successfully!",
          displayAs: "snackbar",
        }),
      );

      // If it's a single product, pass the first item of the array
      // If it's multiple products, pass the entire array
      onCallback(true, data.length === 1 ? res.data.data[0] : res.data.data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "An error occurred",
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertExcelPreviewAsync =
  (
    data: IExpenseExcelPreview,
    onCallback: (isSuccess: boolean, category?: IExpenseCategory) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      // const { ...payload } = data;

      dispatch(saveLoaderProgress());
      const res = await api.post("/expense/export-finance-expense", data);

      const result = res.data.data;

      // let message = "Category created successfully!";
      onCallback(true, result);
      dispatch(
        showMessage({
          type: "success",
          displayAs: "snackbar",
          message: "Excel Preview data fetched successfully!",
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
export const FETCH_DYNAMIC_APPROVAL_HISTORY_PROGRESS =
  "FETCH_DYNAMIC_APPROVAL_HISTORY_PROGRESS";
export const FETCH_DYNAMIC_APPROVAL_HISTORY_SUCCESS =
  "FETCH_DYNAMIC_APPROVAL_HISTORY_SUCCESS";
export const FETCH_DYNAMIC_APPROVAL_HISTORY_FAILED =
  "FETCH_DYNAMIC_APPROVAL_HISTORY_FAILED";

export const fetchDynamicApprovalHistoryProgress = () =>
  action(FETCH_DYNAMIC_APPROVAL_HISTORY_PROGRESS);
export const fetchDynamicApprovalHistorySuccess = (
  data: any[],
  totalRecords: number,
) => action(FETCH_DYNAMIC_APPROVAL_HISTORY_SUCCESS, { data, totalRecords });
export const fetchDynamicApprovalHistoryFailed = () =>
  action(FETCH_DYNAMIC_APPROVAL_HISTORY_FAILED);

export const fetchDynamicApprovalHistoryAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    searchValue: IDatatableV2AdvancedSearchFilter,
    searchByProject?: string,
    searchByCreated?: string,
    searchByExpense?: string,
    dynamic_uuid?: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      let url = "";

      if (status !== "-1") {
        url += `&status=${status}`;
      }
      if (dynamic_uuid) {
        url += `&dynamic_uuid=${dynamic_uuid}`;
      }
      if (searchValue.length > 0) {
        url += `&advanceFilter=${JSON.stringify(searchValue)}`;
      }

      if (searchByProject) {
        url += `&project_uuid=${searchByProject}`;
      }
      if (searchByCreated) {
        url += `&searchByCreated=${searchByCreated}`;
      }
      if (searchByExpense) {
        url += `&user_name=${searchByExpense}`;
      }

      const finalUrl =
        `/dynamicApproval/get-dynamic-approval-history?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}` +
        url;

      // Make the API call
      const res = await api.get(finalUrl);
      const data: any[] = res.data.data;
      const totalRecords = res.data.totalRecords;
      dispatch(fetchDynamicApprovalHistorySuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchDynamicApprovalHistoryFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const fetchApprovalHistoryAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    searchValue: IDatatableV2AdvancedSearchFilter,
    approval_uuid?: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      let url = "";

      if (status !== "-1") {
        url += `&status=${status}`;
      }
      if (approval_uuid) {
        url += `&approval_uuid=${approval_uuid}`;
      }
      if (searchValue.length > 0) {
        url += `&advanceFilter=${JSON.stringify(searchValue)}`;
      }

      const finalUrl =
        `/approval/get-approval-history?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}` +
        url;

      // Make the API call
      const res = await api.get(finalUrl);
      const data: any[] = res.data.data;
      const totalRecords = res.data.totalRecords;
      dispatch(fetchDynamicApprovalHistorySuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchDynamicApprovalHistoryFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const fetchExpenseDownloadPdfAsync =
  (
    uuid: string,

    onCallback: (isSuccess: boolean) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.get(
        `/expense/get-preview-expense?expense_uuid=${uuid}&isPreview=false`,
        {
          responseType: "arraybuffer",
          validateStatus: (status) => status === 200,
        },
      );

      const contentType = res.headers["content-type"];

      if (!contentType.includes("application/pdf")) {
        throw new Error(`Invalid content type: ${contentType}`);
      }

      dispatch(saveLoaderCompleted());

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "expense.pdf";
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);

      onCallback(true);
    } catch (err: any) {
      dispatch(saveLoaderCompleted());

      let errorMessage = "Failed to download PDF";
      if (err.response?.data) {
        const decoder = new TextDecoder("utf-8");
        const errorData = decoder.decode(err.response.data);
        try {
          errorMessage = JSON.parse(errorData).message || errorMessage;
        } catch {
          errorMessage = errorData;
        }
      }

      dispatch(
        showMessage({
          type: "error",
          message: errorMessage,
          displayAs: "snackbar",
        }),
      );
      onCallback(false);
    }
  };

export const FETCH_APPROVAL_WORKFLOW_PROGRESS =
  "FETCH_APPROVAL_WORKFLOW_PROGRESS";
export const FETCH_APPROVAL_WORKFLOW_SUCCESS =
  "FETCH_APPROVAL_WORKFLOW_SUCCESS";
export const FETCH_APPROVAL_WORKFLOW_FAILED = "FETCH_APPROVAL_WORKFLOW_FAILED";

export const fetchApprovalWorkflowProgress = () =>
  action(FETCH_APPROVAL_WORKFLOW_PROGRESS);
export const fetchApprovalWorkflowSuccess = (data: IApprovalFlow) =>
  action(FETCH_APPROVAL_WORKFLOW_SUCCESS, { data });
export const fetchApprovalWorkflowFailed = (errorMessage: string) =>
  action(FETCH_APPROVAL_WORKFLOW_FAILED, { errorMessage });

export const fetchApprovalFlowAsync =
  (uuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchApprovalWorkflowProgress());
      const url = `/expense/get-expense-approval-workflow?expense_uuid=${uuid}`;

      const res = await api.get(url);
      console.log(res, "res");
      const data: IApprovalFlow = res.data.data.workflow;

      dispatch(fetchApprovalWorkflowSuccess(data));
    } catch (err: any) {
      dispatch(fetchApprovalWorkflowFailed("Something went wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "An error occurred",
          displayAs: "snackbar",
        }),
      );
    }
  };
